import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const arraySupport = require('dayjs/plugin/arraySupport');

dayjs.extend(arraySupport);
dayjs.extend(utc);
dayjs.extend(timezone);

export const parseEvent = (event) => {
  const {
    id,
    wedding_id,
    all_day,
    busy,
    summary,
    start_timetuple,
    end_timetuple,
    start_timezone,
    end_timezone,
    calendar_id,
    login_calendar_id,
    display_tz,
    location,
    event_location,
    description,
    is_external,
    when_type,
    conferencing_url,
    conferencing_provider,
    html_link,
  } = event;
  const startTime = convertTimeTuple(start_timetuple, !all_day ? display_tz : null);
  const endTime = convertTimeTuple(end_timetuple, !all_day ? display_tz : null);


  const normalizedEvent = {
    id,
    weddingId: wedding_id,
    type: is_external ? 'external' : 'wedding',
    allDay: all_day,
    busy,
    title: summary,
    ... (!endTime && startTime) && {
      start: startTime,
      end: endTime,
    },
    ... (endTime && startTime) && {
      start: startTime,
      end: endTime,
    },
    startTz: start_timezone,
    endTz: end_timezone,
    externalCalendarId: calendar_id,
    loginCalendarId: login_calendar_id,
    isExternal: is_external,
    location,
    googleLocation: event_location,
    notes: description,
    displayTz: display_tz,
    whenType: when_type,
    conferencingProvider: conferencing_provider,
    conferencingUrl: conferencing_url,
    htmlLink: html_link,
  };
  return normalizedEvent;
};

export const parseChecklistEvent = (event, assignments = {}) => {
  const {
    id,
    wedding_id,
    lead_id,
    lead,
    details,
    timeframe,
    assignment_note,
    is_private,
    client_visible,
    name,
    due_date,
    task_id: taskId,
    completed,
    planning_categories: planningCategories,
  } = event;
  const start = due_date ? dayjs.utc(due_date).toISOString() : null;
  const resourceIds = (taskId && assignments[taskId]) ? assignments[taskId].map(a => a.loginId) : null;

  const normalizedEvent = {
    id,
    // lead task type
    ...(lead_id && {
      projectName: lead?.project_name,
      weddingDate: lead?.wedding_date,
      leadId: lead_id,
      leadFullName: lead?.contact_full_name,
      leadStatus: lead.custom_status.name,
      orgId: lead?.organization_id,
      // location: lead?.location,
    }),
    weddingId: wedding_id,
    clientVisible: client_visible,
    type: 'checklist',
    dueDate: due_date,
    startEditable: true,
    taskId,
    details,
    timeframe,
    assignmentNote: assignment_note,
    allDay: true,
    transparent: true,
    isPrivate: is_private,
    title: name,
    completed,
    assigned: assignments[taskId] || [],
    ... start && {
      start,
      end: start,
    },
    planningCategories: planningCategories ? planningCategories.map(cat => cat.category) : null,
    ... resourceIds && { resourceIds },
  };
  return normalizedEvent;
};

export const parsePaymentEvent = (event) => {
  const {
    id,
    due_date,
    vendor,
    amount,
    paid_on,
    wedding,
    invoice,
  } = event;
  const title = vendor.name ? `Payment to ${vendor.name}` : 'Payment due';
  const start = due_date ? dayjs.utc(due_date).toISOString() : null;
  const normalizedEvent = {
    id,
    weddingId: vendor.wedding_id,
    weddingCurrency: wedding.currency,
    weddingName: wedding.name,
    type: 'payment_due',
    allDay: true,
    transparent: true,
    title,
    amount,
    paidOn: paid_on,
    dueDate: due_date,
    ... start && {
      start,
      end: start,
    },
    vendorName: vendor.name,
    vendorCategories: vendor.categories,
    dependsOnFinalGuestCount: invoice.depends_on_final_guest_count,
    invoicePayments: invoice.payments,
  };
  return normalizedEvent;
};

export const parseBookingEvent = (event) => {
  const {
    id,
    wedding_id,
    wedding_date,
    all_day,
    contact_first_name,
    contact_last_name,
    project_name,
    start_time,
    end_time,
    timezone: display_tz,
  } = event;

  const allDay = all_day || !(start_time && end_time);
  const contactName = [contact_first_name, contact_last_name].filter(Boolean).join(' ');
  const projectName = project_name ? `(${project_name})` : '';
  const title = `Tentative Booking: ${contactName}${projectName}`;

  const start = normalizeEventDateTime(wedding_date, start_time, display_tz, allDay);
  const end = normalizeEventDateTime(wedding_date, end_time, display_tz, allDay);

  const normalizedEvent = {
    id,
    weddingId: wedding_id,
    type: 'booking',
    allDay,
    title,
    start,
    end,
  };
  return normalizedEvent;
};

export const parseWeddingDateEvent = (event) => {
  const {
    all_day_event: allDayEvent,
    start_time: startTime,
    end_time: endTime,
    wedding,
  } = event;

  const {
    id,
    wedding_date: weddingDate,
    name,
    timezone: displayTz,
    service_type: serviceType,
    wedding_address: weddingAddress,
    location,
    estimated_guest_count: weddingGuestCount,
    wedding_partners,
    image_306: image306,
  } = wedding || {};

  const weddingLocation = location
    ? {
        ...location,
        formattedAddress: location?.formatted_address ?? '',
      }
    : null;

  const weddingPartners = Array.isArray(wedding_partners)
    ? wedding_partners.map((partner) => ({
        loginId: partner.login_id,
        name: partner.name,
        isLead: partner.is_lead,
      }))
    : [];

  const allDay = allDayEvent || !(startTime && endTime);

  const start = normalizeEventDateTime(weddingDate, startTime, displayTz, allDay);
  const end = normalizeEventDateTime(weddingDate, endTime, displayTz, allDay);

  const normalizedEvent = {
    id: `booking-event-${id}`,
    type: 'project_date',
    allDay,
    title: name,
    start,
    end,
    serviceType,
    weddingAddress,
    weddingLocation,
    weddingGuestCount,
    weddingPartners,
    projectId: id,
    image306,
    displayTz,
  };
  return normalizedEvent;
};

const normalizeEventDateTime = (date, time, tz, allDay) => {
  if (!date) return null;
  if (allDay) return dayjs.utc(date).toISOString();
  if (time) {
    const dateTime = `${date} ${time}`;
    return tz ? dayjs.tz(dateTime, tz).toISOString() : dayjs.utc(dateTime).toISOString();
  }
  return dayjs.utc(date).toISOString();
};

const convertTimeTuple = (tuple, tz) => {
  if (!tuple) return;
  const timeArray = [
    tuple[0], // year
    tuple[1] - 1, // month
    tuple[2], // day
    tuple[3] || 0, // hour
    tuple[4] || 0, // minute
  ];

  let dateObj;

  try {
    if (tz) {
      dateObj = dayjs.tz(`${timeArray[0]}-${timeArray[1] + 1}-${timeArray[2]} ${timeArray[3]}:${timeArray[4]}`, tz);
    } else {
      dateObj = dayjs.utc(timeArray);
    }

    return dateObj.toISOString();
  } catch {
    return null;
  }
};
